/**
 * Utility functions for detecting if the page is loaded in an iframe
 */

/**
 * Checks if the current page is loaded inside an iframe
 * @returns {boolean} true if the page is in an iframe, false otherwise
 */
export const isInIframe = (): boolean => {
  try {
    // Method 1: Check if window.self !== window.top
    // This is the most reliable method
    return window.self !== window.top;
  } catch (e) {
    // Method 2: If the above throws an error (cross-origin), 
    // it usually means we're in an iframe with different origin
    return true;
  }
};

/**
 * Checks if the current page is loaded inside an iframe with additional context
 * @returns {object} Object containing iframe detection results and additional info
 */
export const getIframeInfo = (): {
  isInIframe: boolean;
  canAccessParent: boolean;
  parentOrigin?: string;
} => {
  const result = {
    isInIframe: false,
    canAccessParent: false,
    parentOrigin: undefined as string | undefined
  };

  try {
    // Check if we're in an iframe
    result.isInIframe = window.self !== window.top;
    
    if (result.isInIframe) {
      // Try to access parent window to check if we can communicate
      try {
        // This will throw if we can't access parent due to cross-origin restrictions
        result.parentOrigin = window.parent.location.origin;
        result.canAccessParent = true;
      } catch (e) {
        result.canAccessParent = false;
      }
    }
  } catch (e) {
    // If we can't even check window.self !== window.top, we're likely in an iframe
    result.isInIframe = true;
    result.canAccessParent = false;
  }

  return result;
};

/**
 * Sends a message to the parent window if we're in an iframe
 * @param message - The message to send
 * @param targetOrigin - The target origin. Defaults to our own origin: the only
 * parent we talk to is the checkout page hosting the WSPay iframe, and `'*'` would
 * hand the message to any site that frames this page.
 */
export const sendMessageToParent = (message: any, targetOrigin: string = window.location.origin): void => {
  if (isInIframe() && window.parent) {
    try {
      window.parent.postMessage(message, targetOrigin);
    } catch (e) {
      console.error('Could not send message to parent window:', e);
    }
  }
};

/**
 * Listens for messages from the parent window
 * @param callback - Function to call when a message is received
 * @returns Function to remove the event listener
 */
export const listenToParentMessages = (callback: (event: MessageEvent) => void): (() => void) => {
  const messageHandler = (event: MessageEvent) => {
    callback(event);
  };

  window.addEventListener('message', messageHandler);

  // Return cleanup function
  return () => {
    window.removeEventListener('message', messageHandler);
  };
};
